/**
 * Le tableau de bord de l'enseignant (docs/12 §6).
 *
 * **Aucun chiffre n'est calculé ici.** Les taux de complétion et d'abandon, la
 * moyenne d'avancement et le taux de réussite d'un exercice viennent du serveur,
 * arrondis une fois : deux écrans qui diviseraient chacun de leur côté
 * afficheraient deux taux différents pour le même cours.
 *
 * Un enseignant sans cours voit un tableau à zéro et non un écran d'erreur :
 * c'est l'état normal du lendemain d'une inscription.
 */
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import {
  useTeacherDashboard,
  type CourseHealth,
  type StrugglingStudent,
  type WeakExercise,
} from "@/features/learning/api/useDashboards";
import { Card } from "@/shared/ui/Card";
import { ErrorState } from "@/shared/ui/ErrorState";
import { Skeleton } from "@/shared/ui/Skeleton";

export function TeacherDashboardPage() {
  const { t } = useTranslation("learning");
  const dashboard = useTeacherDashboard();

  if (dashboard.isPending) {
    return <Skeleton className="mx-auto my-8 h-96 w-full max-w-5xl" label={t("teaching.title")} />;
  }
  if (dashboard.isError) {
    return (
      <div className="mx-auto max-w-5xl px-4 py-8">
        <ErrorState error={dashboard.error} />
      </div>
    );
  }

  const data = dashboard.data;

  return (
    <div className="mx-auto w-full max-w-5xl space-y-6 px-4 py-8">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold">{t("teaching.title")}</h1>
        <p className="text-slate-600 dark:text-slate-400">{t("teaching.subtitle")}</p>
      </header>

      <div className="grid gap-4 sm:grid-cols-3">
        <Stat label={t("teaching.students")} value={String(data.student_count)} />
        <Stat label={t("teaching.activeStudents")} value={String(data.active_student_count)} />
        <Card title={t("teaching.pending")}>
          <p className="text-2xl font-semibold">{data.pending_count}</p>
          {/* Le compteur seul n'appelle rien ; accompagné de la plus vieille
              copie, il appelle une soirée de correction. Les deux viennent
              ensemble du serveur, et ils s'affichent ensemble. */}
          {data.pending_count > 0 ? (
            <Link to="/teach/grading" className="text-brand-600 text-sm hover:underline">
              {t("teaching.openQueue")}
            </Link>
          ) : null}
        </Card>
      </div>

      <Card title={t("teaching.courses")}>
        {data.courses.length === 0 ? (
          <p className="text-sm text-slate-500 dark:text-slate-400">{t("teaching.noCourse")}</p>
        ) : (
          <ul className="space-y-3">
            {data.courses.map((course) => (
              <li key={course.course_id}>
                <CourseRow course={course} />
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card title={t("teaching.struggling")}>
        {data.struggling.length === 0 ? (
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {t("teaching.strugglingEmpty")}
          </p>
        ) : (
          <ul className="space-y-3">
            {data.struggling.map((student) => (
              <li key={`${student.enrollment_id}-${student.reason}`}>
                <StrugglingRow student={student} />
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card title={t("teaching.weakest")}>
        {data.weakest_exercises.length === 0 ? (
          <p className="text-sm text-slate-500 dark:text-slate-400">{t("teaching.weakestEmpty")}</p>
        ) : (
          <ul className="space-y-2">
            {data.weakest_exercises.map((exercise) => (
              <li key={exercise.exercise_id}>
                <WeakExerciseRow exercise={exercise} />
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <Card title={label}>
      <p className="text-2xl font-semibold">{value}</p>
    </Card>
  );
}

function CourseRow({ course }: { course: CourseHealth }) {
  const { t } = useTranslation("learning");
  return (
    <div className="space-y-1">
      <p className="font-medium">
        {course.course_title}
        {course.is_published ? null : (
          <span className="ml-2 text-xs text-slate-500 dark:text-slate-400">
            {t("teaching.draft")}
          </span>
        )}
      </p>
      <p className="text-sm text-slate-600 dark:text-slate-400">
        {t("teaching.courseStats", {
          students: course.student_count,
          progress: course.average_progress,
          completion: course.completion_rate,
          drop: course.drop_rate,
        })}
      </p>
    </div>
  );
}

/**
 * Un élève signalé — **avec son motif**.
 *
 * `reason` gouverne le message parce que les deux appellent des gestes
 * opposés : `inactive` dit de relancer, `repeated_failures` dit d'expliquer ou
 * de rendre un essai. Un message unique en rendrait un des deux faux — c'est la
 * même raison qui fait voyager `lock_reason` jusqu'à l'élève.
 */
function StrugglingRow({ student }: { student: StrugglingStudent }) {
  const { t } = useTranslation("learning");
  return (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <div>
        <p className="font-medium">{student.student_name}</p>
        <p className="text-sm text-slate-600 dark:text-slate-400">
          {student.reason === "repeated_failures"
            ? // `attempts` et non `count` : `count` est le nom qu'i18next
              // réserve à la pluralisation, et il irait chercher des clés
              // `_one` / `_other` qui n'existent pas. Le seuil étant de trois,
              // il n'y a de toute façon jamais de singulier.
              t("teaching.reasonFailures", {
                attempts: student.failed_attempts,
                exercise: student.exercise_title ?? "",
              })
            : t("teaching.reasonInactive", { course: student.course_title })}
        </p>
      </div>
      <Link
        to="/teach/students/$studentId"
        params={{ studentId: student.student_id }}
        className="text-brand-600 text-sm hover:underline"
      >
        {t("teaching.openStudent")}
      </Link>
    </div>
  );
}

function WeakExerciseRow({ exercise }: { exercise: WeakExercise }) {
  const { t } = useTranslation("learning");
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-2 text-sm">
      <span>
        <span className="font-medium">{exercise.exercise_title}</span>
        <span className="text-slate-500 dark:text-slate-400"> — {exercise.course_title}</span>
      </span>
      <span>
        {t("teaching.passRate", {
          rate: exercise.pass_rate,
          passed: exercise.passed_count,
          total: exercise.learner_count,
        })}
      </span>
    </div>
  );
}
